import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../api';

const fontFamily = 'Quicksand, Montserrat, Arial, sans-serif';

function ConfiguracionEmpresa({ token }) {
  const [settings, setSettings] = useState({ direccionEmpresa: '', latitud: '', longitud: '', margenMetros: 200 });
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null); // { tipo: 'ok'|'error', texto }
  const [geocodificando, setGeocodificando] = useState(false);
  const [geocoError, setGeocoError] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/api/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setSettings({
          direccionEmpresa: r.data.direccionEmpresa || '',
          latitud: r.data.latitud ?? '',
          longitud: r.data.longitud ?? '',
          margenMetros: r.data.margenMetros ?? 200
        });
      })
      .catch(() => {});
  }, [token]);

  const geocodificar = async () => {
    if (!settings.direccionEmpresa.trim()) return;
    setGeocodificando(true);
    setGeocoError(null);
    try {
      // Nominatim es gratuito y de código abierto (OpenStreetMap). No requiere API key.
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(settings.direccionEmpresa)}`;
      const res = await fetch(url, {
        headers: { 'Accept-Language': 'es', 'User-Agent': 'FicharApp/1.0' }
      });
      const data = await res.json();
      if (data && data.length > 0) {
        setSettings(s => ({ ...s, latitud: parseFloat(data[0].lat), longitud: parseFloat(data[0].lon) }));
      } else {
        setGeocoError('No se encontró la dirección. Introduce las coordenadas manualmente.');
      }
    } catch {
      setGeocoError('Error al buscar la dirección. Comprueba la conexión a Internet.');
    } finally {
      setGeocodificando(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    try {
      await axios.put(`${API_BASE}/api/settings`, {
        direccionEmpresa: settings.direccionEmpresa,
        latitud: settings.latitud !== '' ? Number(settings.latitud) : null,
        longitud: settings.longitud !== '' ? Number(settings.longitud) : null,
        margenMetros: Number(settings.margenMetros) || 200
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMensaje({ tipo: 'ok', texto: 'Configuración guardada correctamente.' });
    } catch {
      setMensaje({ tipo: 'error', texto: 'Error al guardar la configuración.' });
    } finally {
      setGuardando(false);
      setTimeout(() => setMensaje(null), 4000);
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.75em 1em', borderRadius: 12,
    border: '2px solid #90caf9', fontFamily, fontSize: '1rem',
    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', fontFamily }}>
      <style>{`
        .cfg-input:focus { border-color: #1976d2 !important; }
        @keyframes cfgFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      <div style={{
        background: 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(144,202,249,0.10) 100%)',
        border: '2px solid rgba(25,118,210,0.18)',
        borderRadius: 20,
        padding: '1.8em 1.5em',
        display: 'flex', flexDirection: 'column', gap: '1.4em',
        animation: 'cfgFadeIn 0.35s ease'
      }}>
        <h3 style={{ margin: 0, color: '#1976d2', fontWeight: 800, fontSize: '1.2em' }}>
          🏢 Dirección de la empresa
        </h3>

        {/* Dirección texto */}
        <div>
          <label style={{ fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.4em' }}>
            Dirección (calle, ciudad, país)
          </label>
          <div style={{ display: 'flex', gap: '0.6em', flexWrap: 'wrap' }}>
            <input
              className="cfg-input"
              type="text"
              placeholder="Ej: Calle Mayor 1, Madrid, España"
              value={settings.direccionEmpresa}
              onChange={e => setSettings(s => ({ ...s, direccionEmpresa: e.target.value }))}
              style={{ ...inputStyle, flex: '1 1 200px' }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); geocodificar(); } }}
            />
            <button
              onClick={geocodificar}
              disabled={geocodificando || !settings.direccionEmpresa.trim()}
              title="Buscar coordenadas con OpenStreetMap (gratis)"
              style={{
                padding: '0.75em 1.2em', borderRadius: 12,
                border: '2px solid #1976d2',
                background: geocodificando ? '#e3f2fd' : 'linear-gradient(135deg,#1976d2,#42a5f5)',
                color: geocodificando ? '#1976d2' : '#fff',
                fontWeight: 700, fontSize: '0.95rem', cursor: geocodificando ? 'wait' : 'pointer',
                whiteSpace: 'nowrap', transition: 'all 0.2s'
              }}
            >
              {geocodificando ? '⏳ Buscando...' : '🔍 Geocodificar'}
            </button>
          </div>
          <p style={{ margin: '0.4em 0 0', fontSize: '0.82em', color: '#64748b' }}>
            Usa OpenStreetMap (Nominatim) — gratuito, sin API key. También puedes introducir las coordenadas manualmente.
          </p>
          {geocoError && (
            <p style={{ margin: '0.5em 0 0', color: '#c62828', fontWeight: 600, fontSize: '0.9em' }}>⚠️ {geocoError}</p>
          )}
        </div>

        {/* Coordenadas */}
        <div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 140px' }}>
            <label style={{ fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.4em' }}>
              🌐 Latitud
            </label>
            <input
              className="cfg-input"
              type="number"
              step="any"
              placeholder="Ej: 40.4168"
              value={settings.latitud}
              onChange={e => setSettings(s => ({ ...s, latitud: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label style={{ fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.4em' }}>
              🌐 Longitud
            </label>
            <input
              className="cfg-input"
              type="number"
              step="any"
              placeholder="Ej: -3.7038"
              value={settings.longitud}
              onChange={e => setSettings(s => ({ ...s, longitud: e.target.value }))}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Vista previa mapa si hay coords */}
        {settings.latitud !== '' && settings.longitud !== '' && (
          <div style={{ borderRadius: 12, overflow: 'hidden', border: '2px solid #90caf9' }}>
            <iframe
              title="Mapa empresa"
              width="100%"
              height="200"
              style={{ border: 'none', display: 'block' }}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(settings.longitud)-0.005},${Number(settings.latitud)-0.005},${Number(settings.longitud)+0.005},${Number(settings.latitud)+0.005}&layer=mapnik&marker=${settings.latitud},${settings.longitud}`}
            />
            <p style={{ margin: 0, padding: '0.4em 0.8em', fontSize: '0.8em', color: '#64748b', background: '#f0f7ff' }}>
              Vista previa OpenStreetMap · {Number(settings.latitud).toFixed(5)}, {Number(settings.longitud).toFixed(5)}
            </p>
          </div>
        )}

        {/* Margen */}
        <div>
          <label style={{ fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.4em' }}>
            📏 Margen de tolerancia (metros)
          </label>
          <input
            className="cfg-input"
            type="number"
            min="1"
            max="10000"
            value={settings.margenMetros}
            onChange={e => setSettings(s => ({ ...s, margenMetros: e.target.value }))}
            style={{ ...inputStyle, maxWidth: 200 }}
          />
          <p style={{ margin: '0.4em 0 0', fontSize: '0.82em', color: '#64748b' }}>
            Si un empleado ficha a más de {settings.margenMetros}m de la empresa, el fichaje se marcará fuera del margen.
          </p>
        </div>

        {/* Botón guardar */}
        <button
          onClick={guardar}
          disabled={guardando}
          style={{
            padding: '0.85em 2em', borderRadius: 14,
            border: '3px solid transparent',
            background: guardando
              ? 'linear-gradient(#e3f2fd,#e3f2fd) padding-box'
              : 'linear-gradient(white,white) padding-box, linear-gradient(135deg,#1976d2,#42a5f5) border-box',
            color: '#1976d2', fontWeight: 800, fontSize: '1.05rem',
            cursor: guardando ? 'wait' : 'pointer',
            transition: 'all 0.25s', alignSelf: 'flex-start',
            boxShadow: '0 4px 14px rgba(25,118,210,0.18)'
          }}
        >
          {guardando ? '⏳ Guardando...' : '💾 Guardar configuración'}
        </button>

        {mensaje && (
          <div style={{
            padding: '0.8em 1.2em', borderRadius: 10, fontWeight: 700,
            background: mensaje.tipo === 'ok' ? 'rgba(76,175,80,0.12)' : 'rgba(244,67,54,0.10)',
            color: mensaje.tipo === 'ok' ? '#2e7d32' : '#c62828',
            border: `2px solid ${mensaje.tipo === 'ok' ? 'rgba(76,175,80,0.3)' : 'rgba(244,67,54,0.3)'}`,
            animation: 'cfgFadeIn 0.3s ease'
          }}>
            {mensaje.tipo === 'ok' ? '✅' : '❌'} {mensaje.texto}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfiguracionEmpresa;
